import { useFormik } from "formik";
import { View, Platform } from "react-native";
import React, { useEffect, useState } from "react";
import Toast from "react-native-toast-message";
import Text from "@/components/shared/AppText";
import AsyncStorage from "@react-native-async-storage/async-storage";
import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";
import { authErrorMessage, useAuth } from "@/components/AuthContext";
import SIWA from "@/components/shared/SIWA";
import SIWG from "@/components/shared/SIWG";
import AppInput from "@/components/shared/AppInput";
import { ForgotPasswordLink } from "@/components/shared/ForgotPasswordLink";
import AppTitle from "@/components/shared/AppTitle";
import OnboardingButton from "@/components/shared/OnboardingButton";
import SignUpContainer from "@/components/shared/SignUpContainer";
import { SignUpPrivacy } from "@/components/shared/SignUpPrivacy";
import * as Yup from "yup";
import { useFocusEffect } from "@react-navigation/native";
import { collectMetadataFromBranchOrURL } from "@/components/globalUtils";
import { LoadingScreen } from "@/components/LoadingScreen";
import {
  clearPendingOnboardingEventNavigation,
  storePendingOnboardingEventNavigation,
} from "@/components/pendingOnboardingEventNavigation";
import { useIsMobileUX } from "@/components/hooks/useIsMobileUX";
import { DONATIONS_ENABLED } from "@/components/donations/config";
import {
  buildSignupMetadata,
  captureFundraiserIntent,
  peekFundraiserIntent,
  readFundraiserIntent,
} from "@/components/donations/fundraiserIntent";
import {
  captureFuneralHomeIntent,
  FUNERAL_TRIAL_CREDITS,
  funeralSignupMetadata,
  peekFuneralHomeIntent,
  readFuneralHomeIntent,
} from "@/components/funeral/funeralHomeIntent";
import { t } from "@/localization";

const fundraiserPageTitle = (intent) =>
  intent?.orgName
    ? t("Set up a live fundraiser for {name}", { name: intent.orgName })
    : t("Set up your live fundraiser");

// Funeral homes from eventlive.pro/funeral-streaming.
const funeralPageTitle = (intent) =>
  intent?.companyName
    ? t("Welcome, {name}", { name: intent.companyName })
    : t("Welcome to EventLive");
const funeralSubtitle = (intent, userExists) =>
  userExists
    ? t("Sign in to schedule your next service.")
    : intent?.trial
    ? t("Your first {count} services are free. No credit card needed.", {
        count: FUNERAL_TRIAL_CREDITS,
      })
    : t("Create your account to schedule your first service.");

const SignUpSignIn = ({ navigation, route }) => {
  const {
    signIn,
    signUp,
    checkUserExists,
    userEmail,
    signInWithSocial,
    branchdata,
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const [passwordEntryShown, setpasswordEntryShown] = useState(false);
  const [userExists, setuserExists] = useState(false);
  const [clientMetadata, setclientMetadata] = useState({});

  const isMobileUX = useIsMobileUX();

  const [pageSubtitle, setPageSubtitle] = useState("");
  const [funeralIntent, setFuneralIntent] = useState(() =>
    peekFuneralHomeIntent()
  );
  const [pageTitle, setPageTitle] = useState(() => {
    const intent = peekFundraiserIntent();
    if (DONATIONS_ENABLED && intent) return fundraiserPageTitle(intent);
    const funeral = peekFuneralHomeIntent();
    if (funeral) return funeralPageTitle(funeral);
    return isMobileUX ? t("Welcome to EventLive") : t("Sign in to EventLive");
  });

  // Queue the onboarding-created event so EventList can resolve it after
  // auth/profile creation finishes. The backend event is not always queryable
  // yet at this point, so we store the alias instead of requiring a slug.
  const storePendingOnboardingEvent = async (metadata) => {
    if (!metadata?.alias || metadata?.skipEventCreation) return;

    await storePendingOnboardingEventNavigation({
      alias: metadata.alias,
      openPaywall: true,
    });
  };

  useFocusEffect(
    React.useCallback(() => {
      (async () => {
        const metadata = await collectMetadataFromBranchOrURL(branchdata);

        // Web captures the nonprofit landing params at app start (App.js);
        // native deep links arrive as route params / Branch metadata.
        if (Platform.OS !== "web") {
          await captureFundraiserIntent({ ...metadata, ...route?.params });
          await captureFuneralHomeIntent({ ...metadata, ...route?.params });
        }
        // Keeps landing-page params (company, pledge ids, the funeral home
        // trial, utm_*) in signup metadata even though the URL has lost them
        // by now (signed-out visitors to /signin?… are routed through
        // /onboarding).
        const intent = await readFundraiserIntent();
        const funeral = await readFuneralHomeIntent();
        setFuneralIntent(funeral);

        const storedValues = await AsyncStorage.getItem("onboardingData");
        const parsed = storedValues ? JSON.parse(storedValues) : null;
        const fullMetadata = buildSignupMetadata({
          metadata: { ...funeralSignupMetadata(funeral), ...metadata },
          // A funeral home's landing details also survive empty onboarding
          // answers, like a nonprofit's.
          intent: intent || funeral,
          onboarding: parsed,
        });
        if (parsed) {
          if (parsed.title && parsed.account_type !== "business") {
            setPageTitle(
              t("Schedule ‘{title}’ and share the link", {
                title: parsed.title,
              })
            );
          }

          if (parsed.account_type === "business") {
            setPageTitle(
              t("Set up your account to try all the features and go live!")
            );
          }

          if (parsed.skipEventCreation) {
            setPageTitle(t("Start your free test"));
            setPageSubtitle(
              t(
                "Stream a test from your phone and watch it like a guest would. Schedule your event when you know the date."
              )
            );
          }
        }

        if (DONATIONS_ENABLED && intent) {
          setPageTitle(fundraiserPageTitle(intent));
        } else if (funeral) {
          setPageTitle(funeralPageTitle(funeral));
        }

        setclientMetadata(fullMetadata);
      })();
    }, [])
  );

  // If email and password just sign in right away
  // route?.params?.email
  // route?.params?.password

  const SignInSchema = Yup.object().shape({
    email: Yup.string().email(t("Invalid email")).required(t("Required")),
    ...(passwordEntryShown
      ? {
          password: Yup.string()
            .min(6, t("Too Short!"))
            .required(t("Required")),
        }
      : {}),
  });

  const f = useFormik({
    initialValues: {
      email:
        route?.params?.email ||
        peekFundraiserIntent()?.email ||
        peekFuneralHomeIntent()?.email ||
        userEmail ||
        "",
      password: route?.params?.password || "",
    },
    validationSchema: SignInSchema,
    onSubmit: async (values, actions) => {
      actions.setSubmitting(true);
      // console.log("VALUES:", values, passwordEntryShown);
      if (!passwordEntryShown) {
        const ex = await checkUserExists(values.email);
        setuserExists(ex);
        setpasswordEntryShown(true);
      } else {
        try {
          if (userExists) {
            console.log("SIGNING IN", values);
            await signIn(values);
          } else {
            const datetime = moment(clientMetadata.event_datetime_utc);
            clientMetadata.event_date = datetime.format("YYYY-MM-DD");
            clientMetadata.event_time = datetime.format("HH:mm");
            const dateWithTimezone = moment.tz(
              `${clientMetadata.event_date} ${clientMetadata.event_time}`,
              clientMetadata.event_timezone
            );
            clientMetadata.event_datetime_utc = dateWithTimezone.toISOString();

            console.log("values", values, clientMetadata);

            // Queue the onboarding-created event before auth flips us into the
            // authenticated stack. EventList resolves the alias once the new
            // event is actually visible to the signed-in user.
            await storePendingOnboardingEvent(clientMetadata);

            // Names from the funeral home landing page, for the new account.
            const funeral = await readFuneralHomeIntent();
            await signUp(
              {
                ...values,
                firstname: values.firstname || funeral?.firstName || undefined,
                lastname: values.lastname || funeral?.lastName || undefined,
              },
              clientMetadata
            );
          }
        } catch (err) {
          // Clear pending navigation on failure so EventList doesn't pick up a
          // stale intent on a future successful sign-in.
          await clearPendingOnboardingEventNavigation();
          actions.setSubmitting(false);
          actions.setStatus({ errorMsg: err.message });

          Toast.show({
            text1: authErrorMessage(err),
            type: "error",
            visibilityTime: 5000,
          });
        }
      }
    },
  });

  // The landing page's work email, when this screen was reached without it in
  // the URL (e.g. after the onboarding steps).
  useEffect(() => {
    let active = true;
    readFundraiserIntent().then((intent) => {
      if (active && intent?.email && !f.values.email) {
        f.setFieldValue("email", intent.email, false);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  // A funeral home typed its email on eventlive.pro a moment ago: go straight
  // to choosing a password (or signing in), once. "Not your email?" still
  // leads back to Google, Apple and the email field.
  const funeralEmailChecked = React.useRef(false);
  useEffect(() => {
    let active = true;
    readFuneralHomeIntent().then(async (intent) => {
      if (!active || !intent?.email || funeralEmailChecked.current) return;
      // A link with another email (e.g. from an email) wins over the landing page.
      const linkEmail = route?.params?.email;
      if (linkEmail && linkEmail !== intent.email) return;
      funeralEmailChecked.current = true;
      try {
        // Also replaces an email remembered from an earlier visit.
        f.setFieldValue("email", intent.email, false);
        const exists = await checkUserExists(intent.email);
        if (!active) return;
        setuserExists(exists);
        setpasswordEntryShown(true);
      } catch (error) {
        // Offline or throttled: the email step is still there.
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const socialSignIn = async (response) => {
    try {
      setLoading(true);

      // Only queue onboarding navigation for new accounts. Returning users
      // signing in via social shouldn't trigger the post-signup paywall flow.
      if (!userExists) {
        await storePendingOnboardingEvent(clientMetadata);
      }

      const profile = await signInWithSocial(response, clientMetadata);
      console.log("PROFILE", profile);
    } catch (err) {
      // Auth failed — clear the pending navigation so EventList doesn't pick
      // up a stale intent.
      await clearPendingOnboardingEventNavigation();
      console.error(err);
      setLoading(false);
    }
  };

  const submitOrLoading = f.isSubmitting || loading;

  return (
    <SignUpContainer
      navigation={navigation}
      route={route}
      keyboardVerticalOffset={120}
    >
      <View
      // style={{
      //   display: "flex",
      //   flex: 1,
      //   flexDirection: "column",
      //   justifyContent:
      //     isMobileUX && passwordEntryShown ? "flex-start" : "center",
      // }}
      >
        {submitOrLoading && <LoadingScreen />}

        {!submitOrLoading &&
          (passwordEntryShown ? (
            <>
              <AppTitle
                text={
                  userExists
                    ? t("Welcome back to EventLive")
                    : funeralIntent
                    ? t("Create your password")
                    : t("Create a password for EventLive")
                }
                style={{ marginTop: 40, marginBottom: funeralIntent ? 8 : 20 }}
              />
              {!!funeralIntent && (
                <Text style={{ marginBottom: 20, color: "#5a6670" }}>
                  {funeralSubtitle(funeralIntent, userExists)}
                </Text>
              )}

              <Text style={{ marginBottom: 10 }}>
                {f.values.email}{" "}
                <Text
                  style={{ textDecorationLine: "underline" }}
                  onPress={() => {
                    setpasswordEntryShown(false);
                  }}
                >
                  {t("Not your email?")}
                </Text>
              </Text>

              <AppInput
                textContentType={"password"}
                secureTextEntry={true}
                onChangeText={f.handleChange("password")}
                onBlur={f.handleBlur("password")}
                value={f.values.password}
                placeholder={
                  userExists ? t("Your Password") : t("Choose Password")
                }
                autoCorrect={false}
                spellCheck={false}
                autoCapitalize={"none"}
                errorMessage={
                  f.errors.password && f.touched.password
                    ? f.errors.password
                    : null
                }
                returnKeyType="go"
                returnKeyLabel={t("Sign In")}
                enablesReturnKeyAutomatically={true}
                onSubmitEditing={f.handleSubmit}
                autoFocus
              />

              {userExists && (
                <ForgotPasswordLink
                  onPress={() => {
                    navigation.navigate("ForgotPassword", {
                      email: f.values.email,
                    });
                  }}
                />
              )}

              <OnboardingButton
                style={{
                  marginTop: 20,
                  marginBottom: 0,
                }}
                primary
                onPress={f.handleSubmit}
                disabled={f.isSubmitting}
                text={t("Continue")}
              />
            </>
          ) : (
            <>
              <AppTitle
                text={pageTitle}
                style={{ marginTop: 40, marginBottom: funeralIntent ? 8 : 20 }}
              />
              {funeralIntent ? (
                <Text style={{ marginBottom: 10, color: "#5a6670" }}>
                  {funeralSubtitle(funeralIntent, false)}
                </Text>
              ) : (
                !!pageSubtitle && (
                  <Text
                    style={{ marginTop: -8, marginBottom: 8, color: "#2E3F52" }}
                  >
                    {pageSubtitle}
                  </Text>
                )
              )}

              {Platform.OS !== "android" && (
                <View style={{ marginVertical: 20 }}>
                  <SIWA onSuccess={socialSignIn} full={true} />
                </View>
              )}

              <View style={{ marginBottom: 20 }}>
                <SIWG onSuccess={socialSignIn} />
              </View>

              <View style={{ paddingVertical: 10, marginBottom: 20 }}>
                <Text style={{ textAlign: "center" }}>{t("or")}</Text>
              </View>
              <View
                style={{
                  flexDirection: isMobileUX ? "column" : "row",
                  justifyContent: "space-between",
                }}
              >
                <AppInput
                  style={{
                    flex: isMobileUX ? undefined : 2,
                    margin: 0,
                    marginRight: isMobileUX ? undefined : 5,
                    height: 62,
                  }}
                  name="username"
                  textContentType="username"
                  keyboardType="email-address"
                  onChangeText={f.handleChange("email")}
                  onBlur={f.handleBlur("email")}
                  value={f.values.email}
                  placeholder={t("Your Email")}
                  autoCorrect={true}
                  spellCheck={false}
                  autoCapitalize="none"
                  autoCompleteType="email"
                  returnKeyType="go"
                  returnKeyLabel={t("Sign In")}
                  enablesReturnKeyAutomatically={true}
                  onSubmitEditing={f.handleSubmit}
                />
                <OnboardingButton
                  style={{
                    flex: isMobileUX ? undefined : 1,
                    marginLeft: isMobileUX ? undefined : 5,
                    alignSelf: "stretch",
                    marginTop: isMobileUX ? 10 : 0,
                    marginBottom: 0,
                    paddingHorizontal: 20,
                  }}
                  primary
                  onPress={f.handleSubmit}
                  disabled={f.isSubmitting}
                  text={t("Continue")}
                />
              </View>
              <SignUpPrivacy />
            </>
          ))}
      </View>
    </SignUpContainer>
  );
};

export default SignUpSignIn;
